import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Loader from '../../../utils/Loader';


const ActivePlanView = ({ activePlan, close }) => {
    const navigate = useNavigate();
    const [customerDetails, setCustomerDetails] = useState([]);
    const [activePlanDetails, setActivePlanDetails] = useState({});
    const [loading, setLoading] = useState(true)

    const fetchData = async () => {
        try {
            setLoading(true)
            const token = localStorage.getItem("token");
            const response = await axios.get(
                `public/api/active/plans/customers/${activePlan.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            setActivePlanDetails(response.data);
            setCustomerDetails(response.data.customers || []);
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem("token");
                navigate("/crm");
            }
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // 📅 Date formatter
    const formatDate = (date) =>
        date ? new Date(date).toLocaleDateString("en-IN") : "-";

    // 🔢 Stats
    const totalCustomers = customerDetails.length;
    const totalChitAmount = customerDetails.reduce(
        (sum, c) => sum + Number(c.total_chit_amount || 0),
        0
    );
    const totalPaid = customerDetails.reduce(
        (sum, c) => sum + Number(c.paid_amount || 0),
        0
    );
    const totalPending = customerDetails.reduce(
        (sum, c) => sum + Number(c.pending_amount || 0),
        0
    );

    const statusBadge = (status) => {
        return status === "active"
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700";
    };

    if (loading) {
        return (
            <Loader/>
        )
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">
                    Active Plan – Customer Details
                </h1>
                <button
                    onClick={close}
                    className="text-gray-500 hover:text-black"
                >
                    ✕
                </button>
            </div>

            {/* Plan Info */}
            <div className="mt-2 text-gray-600 text-sm flex flex-wrap gap-4">
                <span>
                    <strong>Plan:</strong> {activePlanDetails?.plan?.name}
                </span>
                <span>
                    <strong>Group:</strong>{" "}
                    {activePlanDetails?.group?.group_name}
                </span>
                <span>
                    <strong>Start Date:</strong>{" "}
                    {formatDate(activePlanDetails?.start_date)}
                </span>
                <span>
                    <strong>End Date:</strong>{" "}
                    {formatDate(activePlanDetails?.end_date)}
                </span>

                <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold capitalize 
    ${statusBadge(activePlanDetails?.status)}`}
                >
                    {activePlanDetails?.status === "active" ? "Active" : "Inactive"}
                </span>
            </div>

            {/* ================= STATS ================= */}
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mt-6">
                <div className="bg-blue-50 p-4 rounded-xl text-center">
                    <p className="text-sm text-gray-500">Customers</p>
                    <p className="text-2xl font-bold text-blue-600">
                        {totalCustomers}
                    </p>
                </div>

                <div className="bg-green-50 p-4 rounded-xl text-center">
                    <p className="text-sm text-gray-500">Total Chit</p>
                    <p className="text-2xl font-bold text-green-600">
                        ₹{totalChitAmount}
                    </p>
                </div>

                <div className="bg-indigo-50 p-4 rounded-xl text-center">
                    <p className="text-sm text-gray-500">Total Paid</p>
                    <p className="text-2xl font-bold text-indigo-600">
                        ₹{totalPaid}
                    </p>
                </div>

                <div className="bg-red-50 p-4 rounded-xl text-center">
                    <p className="text-sm text-gray-500">Total Pending</p>
                    <p className="text-2xl font-bold text-red-600">
                        ₹{totalPending}
                    </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-xl text-center">
                    <p className="text-sm text-gray-500">Plan Amount</p>
                    <p className="text-2xl font-bold text-purple-600">
                        ₹{activePlanDetails?.plan?.amount}
                    </p>
                </div>
            </div>

            {/* ================= TABLE ================= */}
            <div className="mt-8 overflow-x-auto">
                <table className="min-w-full border rounded-xl overflow-hidden">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-4 py-2 text-left text-sm">Name</th>
                            <th className="px-4 py-2 text-left text-sm">Phone</th>
                            <th className="px-4 py-2 text-right text-sm">Total</th>
                            <th className="px-4 py-2 text-right text-sm">Paid</th>
                            <th className="px-4 py-2 text-right text-sm">Pending</th>
                            <th className="px-4 py-2 text-center text-sm">
                                Pending Months
                            </th>
                            <th className="px-4 py-2 text-center text-sm">
                                Payout Amount
                            </th>
                            <th className="px-4 py-2 text-center text-sm">
                                Payment Status
                            </th>

                        </tr>
                    </thead>

                    <tbody>
                        {customerDetails.map((c) => {
                            const payout = c.customer_payouts?.[0]; // latest/first payout
                            const payoutAmount = payout ? payout.amount : 0;
                            const isPaid = !!payout;

                            return (
                                <tr key={c.id} className="border-t hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium">
                                        {c.name}
                                    </td>

                                    <td className="px-4 py-2">
                                        {c.phone}
                                    </td>

                                    <td className="px-4 py-2 text-right">
                                        ₹{c.total_chit_amount}
                                    </td>

                                    <td className="px-4 py-2 text-right text-green-600">
                                        ₹{c.paid_amount}
                                    </td>

                                    <td className="px-4 py-2 text-right text-red-600">
                                        ₹{c.pending_amount}
                                    </td>

                                    <td className="px-4 py-2 text-center">
                                        {c.pending_month_count}
                                    </td>

                                    {/* ✅ NEW PAYOUT AMOUNT */}
                                    <td className="px-4 py-2 text-center font-semibold text-indigo-600">
                                        {payout ? `₹${payoutAmount}` : "-"}
                                    </td>

                                    {/* ✅ STATUS */}
                                    <td className="px-4 py-2 text-center">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-semibold
              ${isPaid
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-red-100 text-red-700"
                                                }`}
                                        >
                                            {isPaid ? "Paid" : "Unpaid"}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>


                </table>
            </div>
        </div>
    );
};

export default ActivePlanView;
